import { memo, useCallback, useEffect, useRef } from "react";
import { useMediaQuery } from "react-responsive";

import { CollapsibleSection, useFeatureFlags } from "@jti/commerce";
import { BREAKPOINT_LARGE } from "@jti/fe-common";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { checkoutV2Slice } from "@jti/store/slices/checkoutV2Slice";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { CheckoutSidebar } from "@checkout-v2/composites/CheckoutSidebar";
import { PageHeader } from "@checkout-v2/composites/PageHeader";
import { CartItemsSummaryContainer } from "@checkout-v2/features/ItemsSummary";
import { useLoyaltyUserSelection } from "@checkout-v2/features/LoyaltyPoints/hooks";
import { PaymentMethodsCartContainer } from "@checkout-v2/features/PaymentMethods";
import { CartConsentAndPayForm } from "@checkout-v2/forms/ConsentAndPayForm";
import { ShippingForm } from "@checkout-v2/forms/ShippingForm";
import { COLLAPSIBLE_SECTION_OPEN_STATE, scrollToElementWithHeaderCompensation } from "@checkout-v2/shared";
import { ConsentAndPaySection } from "@checkout-v2/shared/components/ConsentAndPaySection";
import { TwoColumnLayout } from "@checkout-v2/shared/components/TwoColumnLayout";
import { useCartItems, useGtmCheckoutBegin } from "@checkout-v2/shared/hooks";
import { getCartItemsCount } from "@checkout-v2/shared/utils/getCartItemsCount";

import { GlobalCheckoutErrorState } from "./components/GlobalCheckoutErrorState/GlobalCheckoutErrorState";
import { ShippingFormDataOverview } from "./components/ShippingFormDataOverview";

export const MainPage = memo(() => {
  const { __ } = useTranslations();

  const sectionsState = useSelector((state) => state.checkoutV2.sectionsState);
  const dispatch = useDispatch();

  useGtmCheckoutBegin();
  useLoyaltyUserSelection();

  const { data: cartItems } = useCartItems();

  const cartItemsCount = getCartItemsCount(cartItems) || 0;

  const isDesktopView = useMediaQuery({ minWidth: BREAKPOINT_LARGE });

  const shippingSectionRef = useRef<HTMLDivElement>(null);
  const paymentSectionRef = useRef<HTMLDivElement>(null);
  const orderConfirmationSectionRef = useRef<HTMLDivElement>(null);

  const { ffCheckoutV2EnableIteration2 } = useFeatureFlags();

  const onShippingSectionEdit = () => {
    dispatch(
      checkoutV2Slice.actions.setSectionsState({
        shippingSection: "editing",
        paymentSection: "rested",
        orderConfirmationSection: "rested",
      })
    );
  };

  const onPaymentSectionEdit = () => {
    dispatch(
      checkoutV2Slice.actions.setSectionsState({
        shippingSection: "completed",
        paymentSection: "editing",
        orderConfirmationSection: "rested",
      })
    );
  };

  const onLoyaltyRedeem = () => {
    dispatch(
      checkoutV2Slice.actions.setSectionsState({
        shippingSection: "completed",
        paymentSection: "editing",
        orderConfirmationSection: "rested",
      })
    );

    scrollToElementWithHeaderCompensation(paymentSectionRef.current);
  };

  const scrollIntoSection = useCallback(() => {
    if (sectionsState.shippingSection === "active" && shippingSectionRef.current) {
      scrollToElementWithHeaderCompensation(shippingSectionRef.current);
      return;
    }

    if (sectionsState.paymentSection === "active" && shippingSectionRef.current) {
      scrollToElementWithHeaderCompensation(shippingSectionRef.current);
      return;
    }

    if (sectionsState.orderConfirmationSection === "active" && paymentSectionRef.current) {
      scrollToElementWithHeaderCompensation(paymentSectionRef.current);
    }
  }, [sectionsState, shippingSectionRef, paymentSectionRef]);

  useEffect(() => {
    scrollIntoSection();
  }, [scrollIntoSection]);

  return (
    <TwoColumnLayout>
      <TwoColumnLayout.Header>
        <PageHeader pageTitle={__("checkout.title")} itemsCount={cartItemsCount} />
      </TwoColumnLayout.Header>

      <TwoColumnLayout.Content>
        <GlobalCheckoutErrorState />

        {!isDesktopView && !ffCheckoutV2EnableIteration2 && <CartItemsSummaryContainer />}

        <CollapsibleSection
          ref={shippingSectionRef}
          title={__("checkout.shipping.section.title")}
          state={sectionsState.shippingSection}
          data-testid="shipping-section"
          onEdit={onShippingSectionEdit}
        >
          {COLLAPSIBLE_SECTION_OPEN_STATE.includes(sectionsState.shippingSection) && <ShippingForm />}
          {sectionsState.shippingSection === "completed" && <ShippingFormDataOverview />}
        </CollapsibleSection>

        <CollapsibleSection
          ref={paymentSectionRef}
          title={__("checkout.payment.section.title")}
          state={sectionsState.paymentSection}
          data-testid="payment-section"
          onEdit={onPaymentSectionEdit}
        >
          {COLLAPSIBLE_SECTION_OPEN_STATE.includes(sectionsState.paymentSection) && (
            <PaymentMethodsCartContainer mode="edit" />
          )}
          {sectionsState.paymentSection === "completed" && (
            <PaymentMethodsCartContainer mode="view" onPaymentSectionEdit={onLoyaltyRedeem} />
          )}
        </CollapsibleSection>

        {isDesktopView && (
          <CollapsibleSection
            ref={orderConfirmationSectionRef}
            title={__("checkout.order_confirmation.section.title")}
            state={sectionsState.orderConfirmationSection}
            data-testid="order-confirmation-section"
          >
            <ConsentAndPaySection>
              <CartConsentAndPayForm />
            </ConsentAndPaySection>
          </CollapsibleSection>
        )}
      </TwoColumnLayout.Content>

      <TwoColumnLayout.Sidebar>
        {isDesktopView && <CheckoutSidebar />}

        {!isDesktopView && (
          <CollapsibleSection
            ref={orderConfirmationSectionRef}
            title={__("checkout.order_confirmation.section.title")}
            state={sectionsState.orderConfirmationSection}
            data-testid="order-confirmation-section"
          >
            <CheckoutSidebar />
            <ConsentAndPaySection>
              <CartConsentAndPayForm />
            </ConsentAndPaySection>
          </CollapsibleSection>
        )}
      </TwoColumnLayout.Sidebar>
    </TwoColumnLayout>
  );
});
